// Calendar new-booking modal open/close helpers — registered via
// context.functions so any element can dispatch them with:
//   el.call('openNewBookingModal', overrides)   // freshBooking() overrides
//   el.call('closeNewBookingModal')
//
// Migrated 2026-08-01 from `root.update({ showNewBooking: true, newBooking:
// freshBooking(overrides) })` to a @symbo.ls/windows custom-template layer —
// mirrors the 2026-06-01 CalProfileModal / CalEventDetailModal migration.
// `root.newBooking` / `root.showNewBooking` are retained as data carriers —
// the body's reactive reads (title autofocus, edit-vs-create header copy,
// the scope-picker save re-trigger) still pull from them.
//
// `overrides` is passed straight to `freshBooking(overrides)` — the exact
// contract every former in-page opener already used (date, invitees, type,
// editingEventId for the edit path, …). A caller that genuinely can't
// express its draft via freshBooking overrides can pass a fully-built
// booking object as `{ __booking }` and it's used as-is instead — prefer
// plain overrides everywhere; nothing in this codebase needs the escape
// hatch today.

import { restoreOpenModalLayer } from './modalLayerLifecycle.js'

const NEW_BOOKING_LAYER_ID = 'cal-new-booking'

const _rootState = (el) =>
  el?.state?.root || el?.getRoot?.()?.state?.root || el?.getRoot?.()?.state || null

export function openNewBookingModal(overrides) {
  const root = _rootState(this)
  if (!root) return
  const booking =
    overrides && overrides.__booking
      ? overrides.__booking
      : this.call('freshBooking', overrides || {})
  // MODAL-X-OVERFLOW-1: an explicit maxHeight keeps the outer ModalWindow
  // ceiling bounded so tall content / a short viewport can't push
  // ModalHeader's Close X off-screen (@symbo.ls/windows defaults maxHeight
  // to null otherwise). Kept as a raw openModal call (not the shared
  // openCustomModal wrapper) — this file's own unit test pins the literal
  // 'openModal' dispatch name.
  this.call('openModal', {
    id: NEW_BOOKING_LAYER_ID,
    template: 'custom',
    // The date + two time inputs need more room than the shared modal's
    // compact default. Keep the wider desktop size responsive on smaller
    // viewports instead of letting the row escape the window.
    width: 'I',
    maxWidth: '92vw',
    maxHeight: '90vh',
    content: { extends: 'CalNewBookingModal' },
    // Mirrors the old `extraClose: { pendingSuggestion: null }` reset —
    // fires on every dismiss path (backdrop click, ESC, X button,
    // programmatic close).
    onClose: () => root.update({ showNewBooking: false, pendingSuggestion: null })
  })
  restoreOpenModalLayer(NEW_BOOKING_LAYER_ID)
  // Push the layer before publishing the legacy URL/form carriers. In recent
  // signal builds, subscribers to showNewBooking can enter a reactive cascade;
  // dispatching first guarantees that cascade cannot swallow the user's click.
  root.update({ newBooking: booking, showNewBooking: true })
}

export function closeNewBookingModal() {
  // showNewBooking + pendingSuggestion are cleared by the layer's onClose
  // callback (see openNewBookingModal above) — this only needs to pop the
  // windows-plugin layer.
  this.call('closeModal', NEW_BOOKING_LAYER_ID)
}
